/**
 * A COMPOSED APP — a look assembled from parts of other looks.
 *
 * Joaquín, 2026-09-03: *"if we want to use some components of coda, notion,
 * obsidian all together we can. that is morph."* And on 2026-09-07, asked
 * plainly whether a person can create an app: they cannot. `LOOKS` is a static
 * record of six, `AppTemplate` is a closed union, and nothing in the product
 * authors either. This file is the first thing that is not true of.
 *
 * ## What a composed app IS, and what it deliberately is not yet
 *
 * It is a look: which nav, which topbar, which views, which search — the
 * fifty-four fields of `LookRecord`, each taken from whichever look the person
 * chose for that PART.
 *
 * It is NOT a new generation. `basedOn` names an existing template, and the
 * generator builds exactly as it does for that one — the modules table, the
 * shape table and the builder switch in `packages/generation` are all keyed by
 * `AppTemplate` and a seventh name would fall through the switch with no
 * builder. Reusing a base's generation is not a limitation dodged, it is what
 * composition MEANS here: the same page, presented as somebody else's app.
 *
 * And the STYLESHEET does compose, which this paragraph used to deny. It said
 * *"until a look is a token set, two looks' CSS cannot be mixed, only their
 * DATA can"* — true when it was written and false by the end of the same day,
 * once `renderCapability` began wrapping a borrowed part in an element wearing
 * the donor's class. 138 of app.fibery's 143 selectors are DESCENDANT
 * selectors, so they reach straight through the wrapper, and
 * `e2e/composed.mjs` reads the borrowed nav's painted background and font size
 * back as the DONOR's rather than the base's. A stale caveat is worse than
 * none: it tells the next session not to attempt something the product
 * already does.
 *
 * What still does NOT compose is smaller and worth naming exactly:
 *
 *   - a look's declarations on the app ROOT — tokens set on `.m-app-<name>`
 *     itself reach the whole shell, not a part, so a borrowed part inherits
 *     the HOST's, which is the intended fit rather than a gap;
 *   - a rule that reaches a part with a CHILD combinator, because the wrapper
 *     is `display:contents` and therefore layout-transparent but still a DOM
 *     generation. Three such rules exist today — measured, not guessed — and
 *     one of them has already been given the wrapped spelling by hand. That
 *     residual is task `0238`, filed rather than left in this comment.
 *
 * Task `0173` is still the pass that makes a look a token set. What changed is
 * why: not "so that CSS can compose at all", but so that a borrowed part fits
 * the host's palette instead of importing the donor's.
 *
 * ## The part vocabulary is DERIVED, never a list
 *
 * A hand-kept map from part to fields is the defect this repository keeps
 * catching — a list of the instances somebody thought of is not the set. So a
 * field's part is the leading lowercase run of its own name: `navTree` and
 * `navFold` are the nav, `topbarStar` and `topbarTitle` are the topbar. A
 * fifty-fifth field named `footerX` is the footer on the day it is written and
 * nothing here changes.
 *
 * Measured over the real record when this was written: 54 fields, grouping
 * into parts led by topbar (5), search (5) and nav (4).
 */
import { LOOKS, type LookRecord } from './looks.js';
import type { AppTemplate } from '@morph/spec';

/** A composed app as it is stored and as the hub reads it. */
export interface ComposedApp {
  /** Collision-free identity, and the open route. `composed:` is the prefix so
   *  a template id and a composed id can never be confused by a `startsWith`
   *  somebody writes later. */
  readonly id: string;
  /** What the hub shows on the door. */
  readonly name: string;
  /**
   * What this app is built ON: one of the six, or ANOTHER COMPOSED APP's id.
   *
   * Composing onto a composed app is how a person gets more than one part
   * without a second kind of gesture — "Notion with Fibery's nav", then that
   * with Coda's topbar. The alternative was a multi-row form, which is a new
   * UI shape for something the record could already express.
   *
   * The GENERATION is always the root's: `rootTemplate` walks to it, because
   * the generator's module table, shape table and builder switch are keyed by
   * the six and a chain of presentations does not change which page was made.
   */
  readonly basedOn: string;
  /** Which pool the door opens, exactly as a hub destination names one. */
  readonly poolId: string;
  /**
   * part -> where that part is taken FROM: one of the six, or another composed
   * app's id. A part named here overrides; every part not named comes from
   * `basedOn`.
   *
   * A composed source is a REFERENCE, not a copy — `resolvePartSource` reads
   * it when the record is built, so an app that changes its own nav changes
   * every app that borrowed one from it. That is what "the same source" means
   * here, and it is the reason this stores an id rather than the template that
   * id happened to resolve to on the day somebody pressed Create.
   */
  readonly parts: Readonly<Record<string, string>>;
  readonly at: number;
}

/** The part a `LookRecord` field belongs to: the leading lowercase run of its
 *  name, with a plural folded onto its singular ONLY where that singular is
 *  itself a part — so `views` joins `viewBarStrip` and `tabs` stays its own,
 *  because no field begins `tab`. Derived from the field set, not decided. */
export const partOf = (field: string, allFields: Iterable<string>): string => {
  const lead = (x: string) => /^[a-z]+/.exec(x)?.[0] ?? x;
  const raw = lead(field);
  if (!raw.endsWith('s')) return raw;
  const singular = raw.slice(0, -1);
  for (const other of allFields) {
    if (other === field) continue;
    if (lead(other) === singular) return singular;
  }
  return raw;
};

/** Every field name the six looks between them declare. The union, not one
 *  look's keys: a look that declares nothing for a field still has a part, and
 *  a merge that walked only the base's keys could never REMOVE a field the
 *  donor does not declare — which is a value, not an absence. */
export const lookFields = (): readonly string[] =>
  [...new Set(Object.values(LOOKS).flatMap((l) => Object.keys(l)))].sort();

/** A chain cannot be longer than this. A person composing by hand does not
 *  build a deeper stack, and a cycle — which storage can hold, because two
 *  entries can name each other — must not hang the hub. */
const MAX_CHAIN = 8;

/** The apps this one is built on, NEAREST FIRST, ending at the last composed
 *  app before the root template. Stops on a cycle rather than following it. */
export const baseChain = (app: ComposedApp, all: readonly ComposedApp[]): ComposedApp[] => {
  const chain: ComposedApp[] = [];
  const seen = new Set<string>([app.id]);
  let at = app;
  while (at.basedOn.startsWith('composed:') && chain.length < MAX_CHAIN) {
    const next = all.find((a) => a.id === at.basedOn);
    if (!next || seen.has(next.id)) break;
    seen.add(next.id); chain.push(next); at = next;
  }
  return chain;
};

/** The template the page is GENERATED as — the root of the chain. Undefined
 *  where the chain is broken or cyclic, which is a stored app that can no
 *  longer be opened rather than one that opens as something arbitrary. */
export const rootTemplate = (
  app: ComposedApp, all: readonly ComposedApp[],
): AppTemplate | undefined => {
  const last = baseChain(app, all).at(-1) ?? app;
  return last.basedOn in LOOKS ? last.basedOn as AppTemplate : undefined;
};

/**
 * Where a part's source finally lands, when the source is itself an app
 * somebody made.
 *
 * "Take Coda's table" is simple. "Take *My Coda*'s table" means: whatever THAT
 * app uses for its table — which is its own part if it names one, and its
 * root's otherwise. Resolved rather than refused, because a person who can
 * base an app on one they made will try to borrow from one too, and a donor
 * list that silently excluded them would be a rule nobody could see.
 *
 * Returns undefined where the chain is broken or cyclic — the same answer
 * `rootTemplate` gives, for the same reason: there is no honest source.
 */
export const resolvePartSource = (
  source: string, part: string, all: readonly ComposedApp[], depth = 0,
): AppTemplate | undefined => {
  if (source in LOOKS) return source as AppTemplate;
  if (depth >= MAX_CHAIN) return undefined;
  const donor = all.find((a) => a.id === source);
  if (!donor) return undefined;
  const named = effectiveParts(donor, all)[part];
  return named !== undefined
    ? resolvePartSource(named, part, all, depth + 1)
    : rootTemplate(donor, all);
};

/** Which look each part FINALLY comes from, applied root-outward so a nearer
 *  layer wins — the same order `composedRecord` merges in, and stated once so
 *  the two cannot disagree about what the app is made of. */
export const effectiveParts = (
  app: ComposedApp, all: readonly ComposedApp[],
): Readonly<Record<string, string>> => {
  const layers = [...baseChain(app, all).reverse(), app];
  const out: Record<string, string> = {};
  for (const layer of layers) Object.assign(out, layer.parts);
  return out;
};

/**
 * The `LookRecord` a composed app presents as.
 *
 * Every field is taken WHOLE from the look that owns its part — including that
 * look's silence. Taking Coda's nav means taking Coda's answer to every `nav*`
 * field, and where Coda declares none the composed app declares none either.
 * A merge that only copied the fields a donor happens to set would give the
 * base's value for the rest, which is a third look nobody asked for.
 */
export const composedRecord = (
  app: ComposedApp, all: readonly ComposedApp[] = readComposedApps(),
): LookRecord => {
  const fields = lookFields();
  const root = rootTemplate(app, all);
  /* A BROKEN CHAIN PRESENTS AS MORPH'S OWN, never as an arbitrary look. The
     base it names is gone or cyclic, so there is no honest answer to "which
     app is this"; borrowing one would be a mix nobody chose. */
  if (root === undefined) return {} as unknown as LookRecord;
  const base = LOOKS[root] as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = { ...base };
  const parts = effectiveParts(app, all);
  for (const field of fields) {
    const part = partOf(field, fields);
    const named = parts[part];
    if (named === undefined) continue;
    /* THE SOURCE MAY BE AN APP SOMEBODY MADE, so it is resolved rather than
       read: "take My Coda's table" is whatever that app uses for its table. */
    const from = resolvePartSource(named, part, all);
    if (from === undefined || from === root) continue;
    const donor = LOOKS[from] as unknown as Record<string, unknown>;
    if (field in donor) out[field] = donor[field];
    else delete out[field];
  }
  return out as unknown as LookRecord;
};

/* ── storage ──────────────────────────────────────────────────────────────
 *
 * One entry per key, the shape `readKeptLayouts` already uses and for its
 * reason: a single JSON array under one key is a clobber whenever two tabs
 * write inside one millisecond, which @Codex filed against the kept shelf.
 */
export const COMPOSED_PREFIX = 'morph:composed:';

export function readComposedApps(): ComposedApp[] {
  const found: ComposedApp[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith(COMPOSED_PREFIX)) continue;
      const one = JSON.parse(localStorage.getItem(key) ?? 'null') as ComposedApp | null;
      /* Shape-checked rather than trusted. A stored record is input: another
         tab, an older build, or a person with the console wrote it, and a
         composed app with no `basedOn` would reach `LOOKS[undefined]` and
         present as nothing at all. */
      if (one && typeof one.id === 'string' && typeof one.name === 'string'
        && typeof one.basedOn === 'string'
        && (one.basedOn in LOOKS || one.basedOn.startsWith('composed:'))
        && typeof one.poolId === 'string' && one.parts && typeof one.parts === 'object') {
        found.push({ ...one, at: typeof one.at === 'number' ? one.at : 0 });
      }
    }
  } catch { return found; }
  return found.sort((a, b) => b.at - a.at);
}

export function writeComposedApp(app: ComposedApp): boolean {
  try {
    localStorage.setItem(COMPOSED_PREFIX + app.id, JSON.stringify(app));
    return true;
  } catch { return false; }
}

export function forgetComposedApp(id: string): void {
  try { localStorage.removeItem(COMPOSED_PREFIX + id); } catch { /* gone is gone */ }
}

/** The composed app an id names, or undefined. The id carries its own prefix,
 *  so this answers `undefined` for a template id without reading storage. */
export const composedById = (id: string | undefined): ComposedApp | undefined =>
  id === undefined || !id.startsWith('composed:')
    ? undefined
    : readComposedApps().find((a) => a.id === id);
